import {memo, useCallback, useEffect, useState, useMemo} from "react";
import {useLazyQuery} from "@apollo/client";
import {Stack} from "@mui/material";
import {grey} from "@mui/material/colors";

import {LOOKUP_SHIFT_CHECK_IN_STATUS} from "../../queries/GraphQLQueries";
import CustomAlert from "../CustomAlert";
import TimeslotUnbooked from "./TimeslotUnbooked";
import {
  buildBookableServiceBookableSessionColor,
  buildBookableServiceLockedSessionColor,
  buildServiceTypeDetails
} from "../../helpers/ServiceTypeUtils";
import {
  createMultiColorBackground,
  createRepeatingPattern,
  isPerformerNotAccreditedForTimeslotWithBookings,
} from "../../helpers/ShiftHelper";
import SessionDetails from "./SessionDetails";
import {TIMESLOT_BORDER_COLOR_DARK_GREY} from "./ShiftItemRenderer";
import SessionBriefDetails from "./SessionBriefDetails";
import {SESSION_TYPE} from "../../domain/SessionType";
import TimeslotContextMenu from "./TimeslotContextMenu";
import {UNBOOKABLE_AND_UNACCREDITED_SERVICE_TYPE_PATTERN} from "../../helpers/ShiftPlanHelper";

export const TIMERANGE_COLOR_FOR_UNACCREDITED_PERFORMERS = grey[500];
export const TIMERANGE_COLOR_FOR_TRAINING_SESSION = grey[900]

const CHECK_IN_STATUS_POLLING_RATE_IN_MILLISECONDS = 30 * 1000;
const FULL_SECTION_HEIGHT_PERCENT = 100;

const renderServiceTypeUnbookableStyle = (item) => {
  const serviceTypeDetails = buildServiceTypeDetails(item);
  const hasMatchingSession = item.session?.serviceType
    && serviceTypeDetails.find(detail => detail.description === item.session.serviceType.description);

  if (item.session?.isBreak || item.session?.sessionType === SESSION_TYPE.TRAINING || hasMatchingSession?.isBookable) {
    return null;
  }
  
  const serviceTypeUnbookablePattern = UNBOOKABLE_AND_UNACCREDITED_SERVICE_TYPE_PATTERN;

  if (hasMatchingSession) {
    return <Stack
      className={'single-service-type-unbookable'}
      sx={{
        '--backgroundColor': serviceTypeUnbookablePattern
      }}/>
  }

  const serviceTypeBookableStatusList = serviceTypeDetails.map(serviceTypeDetail => serviceTypeDetail.isBookable);
  
  const sectionHeight = FULL_SECTION_HEIGHT_PERCENT / serviceTypeBookableStatusList.length;

  return serviceTypeBookableStatusList.map((isBookable, index) => {
    if (isBookable) {
      return null;
    }

    return <Stack
      key={index}
      className={'multiple-service-type-unbookable'}
      sx={{
        '--backgroundColor': serviceTypeUnbookablePattern,
        '--index': index,
        '--sectionHeight': sectionHeight
      }}/>
  });
};

function TimeslotDetailedItemRenderer({ item, itemContext, getItemProps, shiftData, onChildDetailsDrilledInto, options }) {
  const defaultOptions = {
    enableContextMenu: false,
    enableSessionHealthDetailDisplay: false,
    enableMeetingRecordingDisplay: false
  };

  const mergedOptions = useMemo(() => ({ ...defaultOptions, ...options }), [options]);
  const isCheckInSession = item.session?.sessionType === SESSION_TYPE.CHECK_IN;


  const [contextMenu, setContextMenu] = useState(null);
  const [checkInStatusInfo, setCheckInStatusInfo] = useState(null);

  const [alertMessage, setAlertMessage] = useState(null);
  const handleCloseCustomAlert = () => {
    setAlertMessage(null);
  };
  

  const [lookupShiftCheckInStatus, { loading: isCheckInDataLoading, data: checkInStatusData }] = useLazyQuery(LOOKUP_SHIFT_CHECK_IN_STATUS, {
    pollInterval: mergedOptions.enablePollingShiftCheckInData ? CHECK_IN_STATUS_POLLING_RATE_IN_MILLISECONDS : 0,
    fetchPolicy: 'network-only',
  });

  
  useEffect(() => {
    if (isCheckInSession) {
      lookupShiftCheckInStatus({variables: {shiftIds: [item.availabilityId]}});
    }
  }, [isCheckInSession, item?.availabilityId]);

  useEffect(() => {
    if (!isCheckInDataLoading) {
      setCheckInStatusInfo(checkInStatusData);
    }
  }, [checkInStatusData, isCheckInDataLoading]);

  const handleContextMenu = (event) => {
    event.preventDefault();
    const itemId = event.target.id;
    if (itemId) {
      if (contextMenu === null && isContextMenuButtonVisible) {
        setContextMenu({
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 6,
          clickedItemId: itemId,
        });
      } else {
        setContextMenu(null);
      }
    }
  };

  const timeRangeBackgroundColor = useCallback((itemContext, item, isHighZoomInView) => {

    const serviceTypeDetails = buildServiceTypeDetails(item);
    const existingSessionServiceType = item.session?.serviceType;
    const allServiceTypesUnbookable = !item.session?.isBookable && serviceTypeDetails.every(detail => detail?.isBookable === false) && !isCheckInSession;
    const isPerformerNotAccreditedForContentsWithinTimeRange = isPerformerNotAccreditedForTimeslotWithBookings(item.session?.from, item.session?.to, item.serviceType) && !isCheckInSession;
    const isTeachingSessionLocked = !item.session?.isBookable && item.isBooked && !isCheckInSession;
    const isTimeRangeUnbookedAndMarkedUnbookable = !item.session?.isBookable && !item.hasSession && !isCheckInSession;
    const isTeachingSessionBooked = item.isBooked && !isCheckInSession;
    const isTimeRangeBookableAndHasSession = item.hasSession && !item.isBooked && item.session?.isBookable && !isCheckInSession;
    const isTimeRangeMarkedUnbookableAndHasSession = item.hasSession && !item.isBooked && !item.session?.isBookable && !isCheckInSession;
    const isLockedSession = item.hasSession && item.session?.isLocked && !isCheckInSession;
    const PATTERN_SIZE_DEFAULT = '0.875rem';
    const PATTERN_SIZE_HIGH_ZOOM = '1.5rem';
    const BLOCKED_SESSION_GREY_COLOR = grey[500];
    const patternSize = isHighZoomInView ? PATTERN_SIZE_HIGH_ZOOM : PATTERN_SIZE_DEFAULT;
    const breakSessionColorPattern = `repeating-conic-gradient( ${BLOCKED_SESSION_GREY_COLOR} 0% 25%, ${BLOCKED_SESSION_GREY_COLOR} 0% 50%) 50%/ ${patternSize} ${patternSize}`;
    const trainingSessionColorPattern = `repeating-conic-gradient( ${TIMERANGE_COLOR_FOR_TRAINING_SESSION} 0% 25%, ${TIMERANGE_COLOR_FOR_TRAINING_SESSION} 0% 50%) 50%/ ${patternSize} ${patternSize}`;
    const blockedTimeRangeColorPattern = `repeating-conic-gradient( ${BLOCKED_SESSION_GREY_COLOR} 0% 25%, timeRangeColor 0% 50%) 50%/ ${patternSize} ${patternSize}`;
    const bookableServiceSessionColorMap = serviceTypeDetails.map(serviceType => buildBookableServiceBookableSessionColor(serviceType));
    const existingBookableServiceSessionColor = buildBookableServiceBookableSessionColor(existingSessionServiceType);
    const existingBookableServiceLockedSessionColor = buildBookableServiceLockedSessionColor(existingSessionServiceType);

    const TIMERANGE_COLOR_BOOKABLE_BOOKED = createMultiColorBackground([existingSessionServiceType?.primaryColor]);
    const TIMERANGE_COLOR_BOOKABLE_UNBOOKED = createMultiColorBackground(bookableServiceSessionColorMap);
    const TIMERANGE_COLOR_UNBOOKABLE = createRepeatingPattern(blockedTimeRangeColorPattern, bookableServiceSessionColorMap);
    const TIMERANGE_COLOR_UNBOOKABLE_BREAK = createRepeatingPattern(breakSessionColorPattern, bookableServiceSessionColorMap);
    const TIMERANGE_COLOR_UNBOOKABLE_TRAINING = createRepeatingPattern(trainingSessionColorPattern, bookableServiceSessionColorMap);
    const TIMERANGE_COLOR_UNBOOKABLE_BOOKED = createRepeatingPattern(blockedTimeRangeColorPattern, [existingSessionServiceType?.primaryColor]);
    const TIMERANGE_COLOR_HAS_SESSION_BOOKABLE = createMultiColorBackground([existingBookableServiceSessionColor]);
    const TIMERANGE_COLOR_HAS_SESSION_UNBOOKABLE = createRepeatingPattern(blockedTimeRangeColorPattern, [existingBookableServiceSessionColor]);
    const TIMERANGE_COLOR_HAS_BOOKED_SESSION_LOCKED = createMultiColorBackground([existingBookableServiceLockedSessionColor]);
    
    const isBreakSession = item.session?.isBreak;
    const isTrainingSession = item.session?.sessionType === SESSION_TYPE.TRAINING;

    let backgroundColor = TIMERANGE_COLOR_BOOKABLE_UNBOOKED;

    if (isCheckInSession) {
      backgroundColor = '';
    } else if (isLockedSession) {
      backgroundColor = TIMERANGE_COLOR_HAS_BOOKED_SESSION_LOCKED;
    } else if (isPerformerNotAccreditedForContentsWithinTimeRange) {
      backgroundColor = TIMERANGE_COLOR_FOR_UNACCREDITED_PERFORMERS;
    } else if (isBreakSession) {
      backgroundColor = TIMERANGE_COLOR_UNBOOKABLE_BREAK;
    } else if (isTrainingSession) {
      backgroundColor = TIMERANGE_COLOR_UNBOOKABLE_TRAINING;
    } else if (isTeachingSessionLocked) {
      backgroundColor = TIMERANGE_COLOR_UNBOOKABLE_BOOKED;
    } else if (isTimeRangeUnbookedAndMarkedUnbookable) {
      backgroundColor = TIMERANGE_COLOR_UNBOOKABLE;
    } else if (isTeachingSessionBooked) {
      backgroundColor = TIMERANGE_COLOR_BOOKABLE_BOOKED;
    } else if (isTimeRangeBookableAndHasSession) {
      backgroundColor = TIMERANGE_COLOR_HAS_SESSION_BOOKABLE;
    } else if (isTimeRangeMarkedUnbookableAndHasSession) {
      backgroundColor = TIMERANGE_COLOR_HAS_SESSION_UNBOOKABLE;
    } else if (allServiceTypesUnbookable) {
      backgroundColor = TIMERANGE_COLOR_UNBOOKABLE;
    }

    return backgroundColor;
  }, [item.id]);

  const availableSeats = item.session?.seatsCount;

  // Step 1 - Define zoom-level dependent properties
  const HIDE_BORDER_WIDTH_BREAKPOINT = 5;
  const MEDIUM_ZOOM_WIDTH_BREAKPOINT = 40;
  const MEDIUM_ZOOM_BRIEF_VIEW_BREAKPOINT = 130;
  const HIGH_ZOOM_WIDTH_BREAKPOINT = 300;
  const DEFAULT_WIDTH = 100;
  const itemWidth = useMemo(() => itemContext ? itemContext.dimensions.width : DEFAULT_WIDTH, [itemContext]);
  const hideBorder = useMemo(() => itemWidth < HIDE_BORDER_WIDTH_BREAKPOINT || isCheckInSession, [itemWidth, isCheckInSession]);
  const showZoomOutView = useMemo(() => itemWidth <= MEDIUM_ZOOM_WIDTH_BREAKPOINT, [itemWidth]);
  const showMediumBriefView = useMemo(() => itemWidth > MEDIUM_ZOOM_WIDTH_BREAKPOINT && itemWidth < MEDIUM_ZOOM_BRIEF_VIEW_BREAKPOINT && availableSeats > 0, [itemWidth, availableSeats]);
  const showMediumZoomView = useMemo(() => itemWidth > MEDIUM_ZOOM_BRIEF_VIEW_BREAKPOINT && itemWidth <= HIGH_ZOOM_WIDTH_BREAKPOINT && availableSeats > 0, [itemWidth, availableSeats]);
  const showHighZoomInView = useMemo(() => itemWidth > HIGH_ZOOM_WIDTH_BREAKPOINT && availableSeats > 0, [itemWidth, availableSeats]);
  const isHighZoomInView = useMemo(() => itemWidth > HIGH_ZOOM_WIDTH_BREAKPOINT, [itemWidth]);
  const background = useMemo(() => timeRangeBackgroundColor(itemContext, item, isHighZoomInView), [itemContext, isHighZoomInView, item]);
  
  const isTeachingSessionBooked = useMemo(() => item.seatsCount > 0, [item.seatsCount]);
  const isContextMenuButtonVisible = useMemo(() => itemWidth > MEDIUM_ZOOM_WIDTH_BREAKPOINT, [itemWidth]);

  const detailedItemContainerStyle = useMemo(() => {
    const style = {
      background: background || '',
      height: "1.25rem",
      padding: "0.125rem",
      color: item.color,
      border: `0.0625rem solid ${TIMESLOT_BORDER_COLOR_DARK_GREY}`
    };

    if (hideBorder) {
      style.border = 0;
    }

    return style;
  }, [background, item.color, hideBorder]);

  return (
    <>
      <Stack
                {...getItemProps({ className: "session-container", title: "", style: detailedItemContainerStyle })}>
                {renderServiceTypeUnbookableStyle(item)}
        {
          <Stack
            id={item.id}
                      style={showZoomOutView ? {height: "100%", width: "100%"} : undefined}
                      onClick={showZoomOutView ? handleContextMenu : undefined}>
            {
              !isTeachingSessionBooked
              &&
              !showZoomOutView
              &&
              <TimeslotUnbooked
                id={item.id}
                options={mergedOptions}
                checkInStatusInfo={checkInStatusInfo}
                contextMenuButtonVisible={isContextMenuButtonVisible}
                onContextMenuClick={handleContextMenu}
                timeslotWrapper={item}/>
            }
            {
              showMediumBriefView
              &&
              <SessionBriefDetails
                options={mergedOptions}
                session={item.session}
                contextId={item.id}
                onContextMenuClick={handleContextMenu}/>
            }
            {
              showMediumZoomView
              &&
              <SessionDetails
                options={mergedOptions}
                session={item.session}
                contextId={item.id}
                onContextMenuClick={handleContextMenu}
                onChildDetailsDrilledInto={onChildDetailsDrilledInto}
                showChildDetailLabel={false}/>
            }
            {
              showHighZoomInView
              &&
              <SessionDetails
                options={mergedOptions}
                session={item.session}
                contextId={item.id}
                onContextMenuClick={handleContextMenu}
                onChildDetailsDrilledInto={onChildDetailsDrilledInto}
                showChildDetailLabel={true}/>
            }
            {
              mergedOptions.enableContextMenu
              && isContextMenuButtonVisible
              && contextMenu !== null
              && !isCheckInSession
              &&
              <TimeslotContextMenu
                contextMenu={contextMenu}
                setContextMenu={setContextMenu}
                setAlertMessage={setAlertMessage}
                shiftData={shiftData}
                event={item}/>
            }
          </Stack>
        }
      </Stack>
      <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
    </>
  )
}

export default memo(TimeslotDetailedItemRenderer);